// Services Supabase pour le système de bénévolat
import { supabase } from './supabaseClient';
import { VolunteerRegistrationForm, VolunteerProfile, EngagementOpportunity } from '../types/volunteer.types';
import { Constituency } from '../data/constituencies';

// =====================================================
// SERVICES CIRCONSCRIPTIONS
// =====================================================

export const constituencyService = {
    // Récupérer toutes les circonscriptions
    async getAll(): Promise<Constituency[]> {
        const { data, error } = await supabase
            .from('constituencies')
            .select('*')
            .order('display_name');
        
        if (error) throw error;
        return data || [];
    },

    // Récupérer les provinces
    async getProvinces(): Promise<string[]> {
        const { data, error } = await supabase
            .from('constituencies')
            .select('province')
            .order('province');
        
        if (error) throw error;
        
        // Retourner uniquement les provinces distinctes
        const provinces = Array.from(new Set(data?.map(item => item.province) || []));
        return provinces;
    },

    // Récupérer les départements par province
    async getDepartmentsByProvince(province: string): Promise<string[]> {
        const { data, error } = await supabase
            .from('constituencies')
            .select('department')
            .eq('province', province)
            .order('department');
        
        if (error) throw error;
        
        const departments = Array.from(new Set(data?.map(item => item.department) || []));
        return departments;
    },

    // Rechercher des circonscriptions
    async search(searchTerm: string, province?: string, department?: string): Promise<Constituency[]> {
        let query = supabase
            .from('constituencies')
            .select('*');

        // Filtres
        if (province) query = query.eq('province', province);
        if (department) query = query.eq('department', department);
        if (searchTerm) {
            query = query.or(`display_name.ilike.%${searchTerm}%,commune.ilike.%${searchTerm}%`);
        }

        const { data, error } = await query.order('display_name');
        
        if (error) throw error;
        return data || [];
    }
};

// =====================================================
// SERVICES BÉNÉVOLAT
// =====================================================

export const volunteerService = {
    // Inscription d'un bénévole
    async register(memberId: string, formData: VolunteerRegistrationForm): Promise<VolunteerProfile> {
        const { data: profile, error: profileError } = await supabase
            .from('volunteer_profiles')
            .insert({
                member_id: memberId,
                experience_level: formData.experience_level,
                previous_experience: formData.previous_experience,
                motivation: formData.motivation,
                status: 'pending'
            })
            .select()
            .single();

        if (profileError) throw profileError;

        const volunteerId = profile.id;

        // Insérer les localités
        for (const locality of formData.localities) {
            const { data: constituency } = await supabase
                .from('constituencies')
                .select('id')
                .eq('province', locality.constituency_id.split('-')[0])
                .eq('department', locality.constituency_id.split('-')[1])
                .eq('commune', locality.constituency_id.split('-')[2])
                .eq('arrondissement', locality.constituency_id.split('-')[3])
                .single();

            if (constituency) {
                for (const relationType of locality.relation_types) {
                    await supabase
                        .from('volunteer_localities')
                        .insert({
                            volunteer_id: volunteerId,
                            constituency_id: constituency.id,
                            relation_type: relationType,
                            is_primary: locality.is_primary
                        });
                }
            }
        }

        // Insérer les types de bénévolat
        if (formData.volunteer_types.length > 0) {
            const { data: types } = await supabase
                .from('volunteer_types')
                .select('id, code')
                .in('code', formData.volunteer_types);

            if (types) {
                const typeInserts = types.map(type => ({
                    volunteer_id: volunteerId,
                    volunteer_type_id: type.id
                }));

                await supabase
                    .from('volunteer_profile_types')
                    .insert(typeInserts);
            }
        }

        // Insérer les compétences
        if (formData.skills.length > 0) {
            const { data: skills } = await supabase
                .from('volunteer_skills')
                .select('id, code')
                .in('code', formData.skills);

            if (skills) {
                const skillInserts = skills.map(skill => ({
                    volunteer_id: volunteerId,
                    skill_id: skill.id,
                    level: 'intermediate' // Par défaut
                }));

                await supabase
                    .from('volunteer_profile_skills')
                    .insert(skillInserts);
            }
        }

        // Insérer les disponibilités
        for (const timeSlot of formData.availability.time_slots) {
            await supabase
                .from('volunteer_availability')
                .insert({
                    volunteer_id: volunteerId,
                    time_slot: timeSlot,
                    frequency: formData.availability.frequency,
                    hours_per_week: formData.availability.hours_per_week
                });
        }

        // Insérer les préférences de contact
        for (const [method, enabled] of Object.entries(formData.contact_preferences)) {
            if (enabled) {
                await supabase
                    .from('volunteer_contact_preferences')
                    .insert({
                        volunteer_id: volunteerId,
                        contact_method: method
                    });
            }
        }

        // Insérer le contact d'urgence
        if (formData.emergency_contact) {
            await supabase
                .from('volunteer_emergency_contacts')
                .insert({
                    volunteer_id: volunteerId,
                    name: formData.emergency_contact.name,
                    phone: formData.emergency_contact.phone,
                    relationship: formData.emergency_contact.relationship
                });
        }

        return profile;
    },

    // Récupérer le profil bénévole d'un membre
    async getByMemberId(memberId: string): Promise<VolunteerProfile | null> {
        const { data, error } = await supabase
            .from('volunteer_details')
            .select('*')
            .eq('member_id', memberId)
            .single();

        if (error) {
            if (error.code === 'PGRST116') return null; // Pas trouvé
            throw error;
        }

        return data;
    },

    // Vérifier si un membre est bénévole
    async isVolunteer(memberId: string): Promise<boolean> {
        const { count } = await supabase
            .from('volunteer_profiles')
            .select('id', { count: 'exact' })
            .eq('member_id', memberId);

        return (count || 0) > 0;
    },

    // Récupérer les statistiques
    async getStats() {
        const { data, error } = await supabase
            .from('volunteer_stats')
            .select('*')
            .single();

        if (error) throw error;
        return data;
    }
};

// =====================================================
// SERVICES OPPORTUNITÉS D'ENGAGEMENT
// =====================================================

export const opportunityService = {
    // Récupérer les opportunités actives
    async getActive(): Promise<EngagementOpportunity[]> {
        const { data, error } = await supabase
            .from('engagement_opportunities')
            .select(`
                *,
                constituencies(display_name)
            `)
            .eq('status', 'published')
            .gte('end_date', new Date().toISOString())
            .order('start_date');

        if (error) throw error;
        return data || [];
    },

    // S'inscrire à une opportunité
    async participate(volunteerId: string, opportunityId: string, notes?: string) {
        const { error } = await supabase
            .from('volunteer_participations')
            .insert({
                volunteer_id: volunteerId,
                opportunity_id: opportunityId,
                status: 'interested',
                notes
            });

        if (error) throw error;
        return true;
    }
};

// =====================================================
// SERVICES PÉTITIONS
// =====================================================

export const petitionService = {
    // Récupérer les pétitions actives
    async getActive() {
        const { data, error } = await supabase
            .from('petitions')
            .select(`
                *,
                constituencies(display_name)
            `)
            .eq('status', 'active')
            .gte('deadline', new Date().toISOString().split('T')[0])
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data || [];
    },

    // Signer une pétition
    async sign(petitionId: string, memberId: string, ipAddress?: string, userAgent?: string) {
        // Vérifier si déjà signé
        const { count } = await supabase
            .from('petition_signatures')
            .select('id', { count: 'exact' })
            .eq('petition_id', petitionId)
            .eq('member_id', memberId);

        if ((count || 0) > 0) {
            throw new Error('Vous avez déjà signé cette pétition');
        }

        const { error } = await supabase
            .from('petition_signatures')
            .insert({
                petition_id: petitionId,
                member_id: memberId,
                ip_address: ipAddress,
                user_agent: userAgent
            });

        if (error) throw error;
        return true;
    },

    // Vérifier si un membre a signé une pétition
    async hasSigned(petitionId: string, memberId: string): Promise<boolean> {
        const { count } = await supabase
            .from('petition_signatures')
            .select('id', { count: 'exact' })
            .eq('petition_id', petitionId)
            .eq('member_id', memberId);

        return (count || 0) > 0;
    }
};

// =====================================================
// UTILITAIRES
// =====================================================

export const volunteerUtils = {
    // Récupérer les types de bénévolat
    async getVolunteerTypes() {
        const { data, error } = await supabase
            .from('volunteer_types')
            .select('*')
            .eq('is_active', true)
            .order('label');

        if (error) throw error;
        return data || [];
    },

    // Récupérer les compétences
    async getSkills() {
        const { data, error } = await supabase
            .from('volunteer_skills')
            .select('*')
            .eq('is_active', true)
            .order('category', { ascending: true })
            .order('label', { ascending: true });

        if (error) throw error;
        return data || [];
    },

    // Récupérer les compétences par catégorie
    async getSkillsByCategory() {
        const skills = await this.getSkills();
        const categories: Record<string, any[]> = {};
        
        skills.forEach(skill => {
            const category = skill.category || 'other';
            if (!categories[category]) categories[category] = [];
            categories[category].push(skill);
        });

        return categories;
    }
};